/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  ComponentContextCreateOptions,
  SingleValueParameter,
  ReportDefinition,
  MultiValueParameter,
  MultiplicityType,
  ParameterBase,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { MDDEResult } from "@sa/rep-mdde";
import { SelectionMode, AxisPosition, AggregationType, DataType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, MDDEQuery, OLAPTransformer, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { CUBE_ID } from "./constants";
import { last } from "lodash";
import { DictionariesService } from "@sa/rep-mdm";
import { clone } from "../../../../shared";

export class ContextReport1 implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  protected _exportParameters = new Map<string, ParameterBase>();
  public netTradeCountries$ = new BehaviorSubject<number[] | null>(null);

  protected cube = new BehaviorSubject<CubeDefinition | null>(null);
  public payloadChart1 = new BehaviorSubject<OLAPResult | null>(null);
  public payloadChart2 = new BehaviorSubject<OLAPResult | null>(null);

  public sources$ = new BehaviorSubject<string | null>(null);

  protected _calendar: BehaviorSubject<string[]>;
  public get calendar(): Observable<string[]> {
    return this._calendar.asObservable();
  }

  protected _cropCalendar: BehaviorSubject<string[]>;
  public get cropCalendar(): Observable<string[]> {
    return this._cropCalendar.asObservable();
  }

  protected _commodities: BehaviorSubject<string[]>;
  public get commodities(): Observable<string[]> {
    return this._commodities.asObservable();
  }

  protected _business: BehaviorSubject<number>;
  public get business(): Observable<number> {
    return this._business.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    const calendarParameter = createOptions.dataflowService.getParameter("@calendar") as MultiValueParameter;
    this._calendar = new BehaviorSubject(calendarParameter.value as string[]);
    const cropCalendarParameter = createOptions.dataflowService.getParameter("@crop_calendar") as MultiValueParameter;
    this._cropCalendar = new BehaviorSubject(cropCalendarParameter.value as string[]);

    const commoditiesParameter = createOptions.dataflowService.getParameter("@commodities") as MultiValueParameter;
    this._commodities = new BehaviorSubject(commoditiesParameter.value as string[]);
    const businessParameter = createOptions.dataflowService.getParameter("@business-units") as SingleValueParameter;
    this._business = new BehaviorSubject(businessParameter.value as number);

    combineLatest([this._calendar, this._cropCalendar, this._commodities, this._business])
      .pipe(debounceTime(500))
      .subscribe(([calendar, cropCalendar, commodities, business]) => {
        if (calendar.length === 1) {
          this.query({ calendar, cropCalendar, commodities, business });
        }
      });
    const exportParameters = [
      "@e_calendar", "@e_crop_calendar", "@e_countries_net_trade", "@commodities", "@business-units"
    ];
    for (const eParamName of exportParameters) {
      let eParameter = this.options.dataflowService.getParameter(eParamName);
      if (eParameter === undefined) {
        this.options.dataflowService.parameters.add({
          id: eParamName,
          multiplicityType: MultiplicityType.Multiple,
          type: DataType.Number,
          allowNullable: false,
          value: []
        });
        eParameter = this.options.dataflowService.getParameter(eParamName);
      }
      this._exportParameters.set(eParamName, eParameter!);
    }
  }

  async tryIntegrate() {
    if (this.dataflowIntegration.canIntegrate && !this.dataflowIntegration.integrated) {
      this.dataflowIntegration.integrated = true;
      const cube = await dataService.getCubeDefinition(CUBE_ID);
      this.cube.next(cube);
    }
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar");
    if (calendar !== undefined) {
      this._calendar.next(calendar as string[]);
    }
    const cropCalendar = command.values.get("@crop_calendar");
    if (cropCalendar !== undefined) {
      this._cropCalendar.next(cropCalendar as string[]);
    }
    const commodities = command.values.get("@commodities");
    if (commodities !== undefined) {
      this._commodities.next(commodities as string[]);
    }
    const business = command.values.get("@business-units");
    if (business !== undefined) {
      this._business.next(business as number);
    }
  }

  async query(parameters: { calendar: string[]; cropCalendar: string[]; commodities: string[]; business: number }) {
    const { calendar, commodities, business } = parameters;
    const calendarId = Number(calendar[0]);

    this.readyState = ComponentContextReadyState.Loading;
    this.payloadChart1.next(null);
    this.payloadChart2.next(null);

    const { dataSourcesService } = this.options;
    const commodityId = Number(commodities[0]);
    const sources = await dataSourcesService.getDataSources(5, business, commodityId);
    this.sources$.next(sources);

    const cubeFactsDef = await dataService.getCubeDefinition("facts_slides");
    const cubeFacts = await dataService.getCube({
      id: "facts_slides",
      parameters: cubeFactsDef.parameters,
    });

    const dictionaryService = this.options.serviceProvider.resolve(ServicesRegister.Dictionary) as DictionariesService;
    const dictSlideCountries = await dictionaryService.getDictionary({
      id: 'slides-countries-display',
      parameters: {
        'slide': 5,
        'olap_commodity': commodityId,
        'business_unit': business
      }
    });
    const dictSlideCountriesItems = await dictSlideCountries.getAll();
    const itemsSlideCountriesRelationship = new Map(dictSlideCountriesItems.map(x => [Number(x.getValue("olap_country")), Number(x.id)]));
    const itemsSlideCountries = dictSlideCountriesItems.map(x => Number(x.getValue("olap_country")));
    let itemsPositiveCountries: number[] | undefined = undefined;
    let itemsNegativeCountries: number[] | undefined = undefined;
    let calendarOffset = 10;
    // Если не смогли вытянуть данные за прогнозируемый год, смотри не дальше чем на выбранный
    while ((itemsPositiveCountries === undefined || !itemsPositiveCountries.length) && calendarOffset >= 0) {
      const query: any = {
        'p_commodity': commodityId,
        'p_calendar': calendarId + (10000 * calendarOffset),
        'p_global_unit': business
      };
      const dictionaryId = 'dict_f_lead_net_trade_countries';
      const dictPositiveCountries = await dictionaryService.getDictionary({
        id: dictionaryId,
        parameters: { ...query, 'p_positive': true, }
      });
      const dictNegativeCountries = await dictionaryService.getDictionary({
        id: dictionaryId,
        parameters: { ...query, 'p_positive': false, }
      });
      itemsPositiveCountries = (await dictPositiveCountries.getAll()).map(x => Number(x.id));
      itemsNegativeCountries = (await dictNegativeCountries.getAll()).map(x => Number(x.id));
      calendarOffset -= 10;
    }

    let itemsSlidePositiveCountries = itemsPositiveCountries?.filter(id => itemsSlideCountries.includes(id)) || [];
    let itemsSlideNegativeCountries = itemsNegativeCountries?.filter(id => itemsSlideCountries.includes(id)) || [];
    if (itemsSlidePositiveCountries.length === 0) {
      itemsSlidePositiveCountries = itemsSlideCountries;
    }
    if (itemsSlideNegativeCountries.length === 0) {
      itemsSlideNegativeCountries = Array.from(itemsSlidePositiveCountries).reverse();
    }
    const positiveCountres = itemsSlidePositiveCountries.slice(0, 5);
    const negativeCountres = (itemsSlideNegativeCountries.slice(0, 5).reverse());
    const countries = positiveCountres.concat(negativeCountres);
    this.netTradeCountries$.next(countries);

    const worldCountry = business === 2 ? 20000 : 100231;

    const cubeSlideDef = await dataService.getCubeDefinition("slide1");
    const chart1Parameters = clone(cubeSlideDef.parameters);
    chart1Parameters["p_slide"]!["defaultValue"] = '1';
    chart1Parameters["p_commodities"]!["defaultValue"] = commodities.join(',');
    chart1Parameters["p_countries"]!["defaultValue"] = [worldCountry].join(',');
    chart1Parameters["p_attributes"]!["defaultValue"] = [1, 17, 22].join(',');
    chart1Parameters["p_calendar"]!["defaultValue"] = [-4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)).join(',');
    chart1Parameters["p_units"]!["defaultValue"] = [1, 7, 8, 9].join(',');
    chart1Parameters["p_business"]!["defaultValue"] = business.toString();

    const cubeSlideChart1 = await dataService.getCube({
      id: "slide1",
      parameters: chart1Parameters,
    });
    const chart2Parameters = clone(cubeSlideDef.parameters);
    chart2Parameters["p_slide"]!["defaultValue"] = '5';
    chart2Parameters["p_commodities"]!["defaultValue"] = commodities.join(',');
    chart2Parameters["p_countries"]!["defaultValue"] = countries.join(',');
    chart2Parameters["p_attributes"]!["defaultValue"] = [27].join(',');
    chart2Parameters["p_calendar"]!["defaultValue"] = [0, +10].map(x => calendarId + (x * 10000)).join(',');
    chart2Parameters["p_units"]!["defaultValue"] = [8].join(',');
    chart2Parameters["p_business"]!["defaultValue"] = business.toString();

    const cubeSlideChart2 = await dataService.getCube({
      id: "slide1",
      parameters: chart2Parameters,
    });
    window['useFacts'] = function () {
      sessionStorage.setItem('facts', 'true');
    };
    window['useFunc'] = function () {
      sessionStorage.removeItem('facts');
    }
    const cubeChart1 = sessionStorage.getItem('facts') ? cubeFacts : cubeSlideChart1;
    const cubeChart2 = sessionStorage.getItem('facts') ? cubeFacts : cubeSlideChart2;


    const eCountriesParameter = this._exportParameters.get("@e_countries_net_trade")! as MultiValueParameter;
    eCountriesParameter.value = countries.map(x => itemsSlideCountriesRelationship.get(x));
    if (eCountriesParameter.value.length === 0) {
      eCountriesParameter.value = [worldCountry];
    }
    const eCalendarParameter = this._exportParameters.get("@e_calendar")! as MultiValueParameter;
    eCalendarParameter.value = [-4, -2, 0, +5, +10].map(x => calendarId + (x * 10000));
    const eCropCalendarParameter = this._exportParameters.get("@e_crop_calendar")! as MultiValueParameter;
    eCropCalendarParameter.value = [0, +10].map(x => calendarId + (x * 10000));

    const olapResult1$ = cubeChart1.query({
      columns: {
        showEmpty: false,
        parts: [
          { path: "calendar.default", selection: [-4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)), selectionMode: SelectionMode.Include },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "countries.default", selection: [worldCountry], selectionMode: SelectionMode.Include },
          { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          { path: "units.default", selection: [8, 9, 7], selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "attributes.default", selection: [1, 17, 22], selectionMode: SelectionMode.Include },
          { path: "Measures", measures: [{ id: "value", aggregationType: AggregationType.Sum }] },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeChart1.id,
      },
    } as any);

    const olapResult2$ = cubeChart2.query({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "countries.default",
            selection: countries,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "attributes.default", selection: [27], selectionMode: SelectionMode.Include },
          { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          { path: "units.default", selection: [8], selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: [0, +10].map(x => calendarId + (x * 10000)),
            selectionMode: SelectionMode.Include,
          },
          {
            path: "Measures",
            measures: [{ id: "value", aggregationType: AggregationType.Sum }],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeChart2.id,
      },
    } as any);

    this.readyState = ComponentContextReadyState.Loaded;
    let [olapResult1, olapResult2] = await Promise.all([olapResult1$, olapResult2$]);
    olapResult1 = dataSourcesService.clearDataSources(olapResult1);
    olapResult2 = dataSourcesService.clearDataSources(olapResult2);
    this.payloadChart1.next(olapResult1);
    this.payloadChart2.next(olapResult2);
  }
}
